// @flow
import React from 'react';
import { H3 } from '@finect/tabular-core/Headings';
import { Paragraph } from '@finect/tabular-core/Paragraph';
import { ArticleDetail, ArticleSide, SideBlockquote, Video, ArticleCTA } from '../../../components';

type Props = {
  staticContext?: Object,
  location: Object,
  history: Object,
  toggleModal: Function
};

export const Casa = ({ location, history, toggleModal }: Props) => {
  const redirectTo = (url: string) => {
    history.push(url);
  };

  return (
    <ArticleDetail
      location={location}
      title="Cómo puede un asesor ayudarte a comprar una casa."
      redirectTo={redirectTo}
      toggleModal={toggleModal}
    >
      <Paragraph>
        Una de las mayores decisiones que tomamos a lo largo de nuestra vida es la compra de una vivienda. También a
        nivel personal, pero sobre todo a nivel financiero. De hecho, suele ser la mayor operación en euros que realizan
        muchas personas al cabo de sus días. Sin embargo, la mayoría de las veces tomamos esta decisión sólos, o
        preguntando sólo a nuestra pareja o familia, que no son precisamente expertos financieros.
      </Paragraph>
      <Paragraph>
        Curiosamente, en España, uno de los países con mayor actividad inmobiliaria, es poco frecuente recurrir a un
        asesor financiero para que nos ayude con esta operación tan importante para nuestra salud financiera. Quizá,
        porque no somos conscientes de hasta qué punto nos puede ser útil. Veamos.
      </Paragraph>
      <H3>¿Comprar o alquilar?</H3>
      <Paragraph>
        Esta decisión es muy personal. Cierto. Pero para tomarla deberíamos tener muy en cuenta los factores financieros
        asociados. De hecho, para muchas personas el argumento principal para comprar y no alquilar es puramente
        financiero: “No tirar el dinero”, que se suele decir.
      </Paragraph>
      <Paragraph>
        Un buen asesor financiero nos ayudará a calcular bien qué implica a nivel económico uno y otro estilo de vida:
        si es mejor una u otra opción en función de la situación del mercado; los riesgos que asumimos si nos metemos en
        una hipoteca; las alternativas que tenemos, si al final alquilamos, con el dinero que tenemos ahorrado, para
        asegurarnos de que podremos seguir pagando el alquiler ante un imprevisto...
      </Paragraph>
      <ArticleCTA toggleModal={toggleModal} />
      <H3>Planificar la operación</H3>
      <Paragraph>
        Imaginemos que nos decantamos por comprar. ¿Pero cuándo hacerlo? Seguro que muchas personas habrían agradecido
        que alguien les hubiera frenado el ímpetu los meses previos al estallido de la crisis en 2008. No sólo por los
        precios, sino por el enorme endeudamiento que había que asumir para llevar a cabo la operación. Sólo visualizar
        en euros el cálculo de los intereses que se acabarían pagando al final de la vida del préstamo más de uno se lo
        habría pensado.
      </Paragraph>
      <ArticleSide position="left">
        <SideBlockquote right>
          La compra de una casa empieza con una planificación del ahorro para poder hacer frente a los gastos derivados
          de la compra.
        </SideBlockquote>
      </ArticleSide>
      <Paragraph>
        Nos puede ayudar también a establecer un plan de ahorro durante unos años previos a la compra, para poder dar
        una mayor entrada y así conseguir mejores condiciones en el préstamo. O a calcular los costes adicionales que
        tendremos que asumir para amueblar la vivienda y tenerlo en cuenta.
      </Paragraph>
      <H3>¿Hipoteca a tipo fijo o variable? Y sin sorpresas.</H3>
      <Video
        inline
        id="_5Cg2h5rSXo"
        src="https://www.youtube.com/embed/_5Cg2h5rSXo?hd=1&showinfo=0&controls=0&rel=0&enablejsapi=1"
        caption="César, inversor particular."
      />
      <Paragraph>
        Por supuesto, a la hora de elegir el crédito hipotecario, su ayuda será esencial para contratar la tipología más
        interesante para nosotros. Por ejemplo, en función de la situación de los tipos de interés, ver si nos interesa
        más una hipoteca a tipo fijo o una variable.
      </Paragraph>
      <ArticleSide position="right">
        <SideBlockquote>
          Un asesor nos ayudará a saber, en función de la situación de los tipos de interés, si nos interesa más una
          hipoteca a tipo fijo o variable.
        </SideBlockquote>
      </ArticleSide>
      <Paragraph>
        Y, por supuesto, no aventurarnos en ‘opciones’ más arriesgadas que hemos sufrido en nuestra historia reciente,
        como las hipotecas multidivisa o con índices de referencia exóticos.
      </Paragraph>
      <Paragraph>
        Ah, y desde luego, no encontrarnos sorpresas inesperadas como los cláusulas suelo que tantos dolores de cabeza
        han dado a los españoles.
      </Paragraph>
      <H3>Elegir la mejor hipoteca del mercado</H3>
      <Paragraph>
        El asesor nos aportará más valor a la hora de seleccionar la mejor hipoteca si es independiente de los bancos y
        puede indagar entre toda la oferta de préstamos disponibles de todas las entidades. Pero incluso si trabaja en
        un banco, ya sea como agente o en plantilla, nos ayudará a conseguir las mejores condiciones dentro de su
        entidad.
      </Paragraph>
      <Paragraph>
        Aunque con los comparadores online podemos ver las más baratas, sin necesidad de ayuda, en muchos casos estos
        préstamos acaban teniendo sorpresas en las condiciones que un buen experto nos ayudará a desentrañar antes de
        contratar.
      </Paragraph>
      <H3>Evitar la contratación de productos innecesarios</H3>
      <Paragraph>
        Con las ofertas de las hipotecas, los bancos suelen meter productos asociados, que nos generan comisiones que
        tendremos que pagar durante muchos años, como seguros o planes de pensiones. Conviene separar el grano de la
        paja, para asegurarnos de que el tipo de interés que nos parece un chollo no acaba siendo un tremendo agujero en
        nuestra cuenta corriente con el paso de los años.
      </Paragraph>
      <ArticleSide position="left">
        <SideBlockquote right>
          Con las ofertas de las hipotecas, es clave conocer bien las condiciones de los productos asociados, como
          seguros o planes de pensiones.
        </SideBlockquote>
      </ArticleSide>
      <Paragraph>
        Nuestro asesor nos ayudará a analizar bien cada producto y analizar si es mejor pagar un tipo más alto pero sin
        atarnos a productos poco eficientes y muy costosos… O a valorar si la oferta del banco es realmente tan
        interesante como parece. O, incluso, a conseguir mejores condiciones negociando con la entidad por nosotros.
      </Paragraph>
      <H3>Trámites burocráticos</H3>
      <Paragraph>
        Solicitar la información del registro de propiedad, asegurarse de que la casa que vamos a comprar no tiene
        cargas, redactar el contrato de compra venta, asegurarnos de que llevamos toda la documentación necesaria a la
        firma… Al final, tener alguien que se encargue de todo esto supone más tiempo para nosotros.
      </Paragraph>
      <ArticleCTA toggleModal={toggleModal} />
      <H3>Y pagando sólo lo justo en impuestos</H3>
      <Paragraph>
        Tanto si compramos como si alquilamos, hay una serie de impuestos que vamos a tener que pagar. O al revés, una
        serie de ventajas fiscales o ayudas que podemos aplicarnos. Un asesor que nos ayude con la fiscalidad de nuestra
        decisión inmobiliaria puede ser vital para que sea más eficiente para nosotros y podamos vivir más tranquilos.
      </Paragraph>
    </ArticleDetail>
  );
};
